import { Trophy } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserRanking } from '@/services/admin/metrics.service'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

const MEDAL_COLORS = [
  'text-amber-400', // 🥇
  'text-zinc-300', // 🥈
  'text-amber-700', // 🥉
]

/**
 * Top 10 usuários mais ativos no período filtrado. Reusa
 * `useUserRanking` existente (do back). Mostra avatar + nome +
 * episódios completados.
 */
export function TopUsersRanking({ filters }: Props) {
  const { data, isLoading } = useUserRanking(filters.courseId, filters.companyIds)

  const top10 = (data ?? []).slice(0, 10)

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Trophy className="w-4 h-4 text-amber-400" />
          Top usuários
        </CardTitle>
        <CardDescription>Mais episódios completos no período</CardDescription>
      </CardHeader>
      <CardContent>
        {top10.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Sem dados pra esse filtro.
          </p>
        ) : (
          <ol className="space-y-2">
            {top10.map((user, idx) => (
              <li
                key={user.userId}
                className="flex items-center gap-3 rounded-lg p-2 hover:bg-card/40 transition-colors"
              >
                <span
                  className={`w-6 text-center text-sm font-bold tabular-nums ${
                    idx < 3 ? MEDAL_COLORS[idx] : 'text-muted-foreground'
                  }`}
                >
                  {idx + 1}
                </span>
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={user.userAvatar ?? undefined} />
                  <AvatarFallback className="text-xs">
                    {user.userName?.slice(0, 2).toUpperCase() ?? '??'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.userName}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{user.userEmail}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-primary">
                  {user.totalCompleted}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}
